/**
 * React hooks for real-time price data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { subscribeToPriceUpdates, getCurrentPrice, getMultiplePrices } from './priceService';

/**
 * Hook to subscribe to real-time price updates for a single symbol
 * @param {string} symbol - The instrument symbol
 * @returns {{ price: object|null, loading: boolean, error: string|null }}
 */
export const usePrice = (symbol) => {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!symbol) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Subscribe to price updates
    const unsubscribe = subscribeToPriceUpdates(symbol, (priceData) => {
      setPrice(priceData);
      setLoading(false);
    });
    
    // Fetch initial price
    getCurrentPrice(symbol)
      .then(data => {
        if (data) {
          setPrice(data);
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
    
    return () => unsubscribe();
  }, [symbol]);
  
  return { price, loading, error };
};

/**
 * Hook to subscribe to multiple prices at once
 * @param {string[]} symbols - Array of instrument symbols
 * @returns {{ prices: object, loading: boolean, error: string|null }}
 */
export const usePrices = (symbols) => {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribesRef = useRef([]);
  
  useEffect(() => {
    if (!symbols || symbols.length === 0) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Cleanup previous subscriptions
    unsubscribesRef.current.forEach(unsub => unsub());
    unsubscribesRef.current = [];
    
    // Subscribe to each symbol
    symbols.forEach(symbol => {
      const unsubscribe = subscribeToPriceUpdates(symbol, (priceData) => {
        setPrices(prev => ({
          ...prev,
          [symbol]: priceData
        }));
      });
      unsubscribesRef.current.push(unsubscribe);
    });
    
    // Fetch initial prices
    getMultiplePrices(symbols)
      .then(data => {
        setPrices(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
    
    return () => {
      unsubscribesRef.current.forEach(unsub => unsub());
      unsubscribesRef.current = [];
    };
  }, [JSON.stringify(symbols)]); // Re-run when symbols array changes
  
  return { prices, loading, error };
};

/**
 * Hook to calculate real-time P&L for positions
 * @param {object[]} positions - Array of position objects
 * @returns {{ positionsWithPL: object[], totalPL: number, loading: boolean }}
 */
export const usePositionsPL = (positions) => {
  const symbols = [...new Set(positions.map(p => p.symbol))];
  const { prices, loading } = usePrices(symbols);
  
  const positionsWithPL = positions.map(pos => {
    const priceData = prices[pos.symbol];
    if (!priceData) return { ...pos, realTimePL: null };
    
    const currentPrice = priceData.price;
    const entryPrice = pos.entry_price;
    const quantity = pos.quantity || pos.amount || 1;
    
    let pl;
    if (pos.side === 'buy' || pos.type === 'BUY') {
      pl = (currentPrice - entryPrice) * quantity;
    } else {
      pl = (entryPrice - currentPrice) * quantity;
    }
    
    return {
      ...pos,
      current_price: currentPrice,
      realTimePL: pl,
      profit_loss: pl,
      priceChange: priceData.change,
      priceChangePercent: priceData.changePercent
    };
  });
  
  const totalPL = positionsWithPL.reduce((sum, p) => sum + (p.realTimePL || 0), 0);
  
  return { positionsWithPL, totalPL, loading };
};

/**
 * Hook for market overview data
 * @returns {{ marketData: object, loading: boolean }}
 */
export const useMarketOverview = () => {
  const defaultSymbols = [
    'EURUSD', 'GBPUSD', 'USDJPY', 'XAUUSD',
    'BTCUSD', 'ETHUSD'
  ];
  
  const { prices, loading, error } = usePrices(defaultSymbols);
  
  const marketData = Object.entries(prices).map(([symbol, data]) => ({
    symbol,
    ...data
  }));
  
  return { marketData, loading, error };
};

export default {
  usePrice,
  usePrices,
  usePositionsPL,
  useMarketOverview
};
